import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for token in localStorage
    const storedToken = localStorage.getItem('invy_token');
    if (storedToken) {
      setToken(storedToken);
      fetchUserInfo(storedToken);
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserInfo = async (authToken) => {
    try {
      // Get base Worker URL (without /api suffix)
      const workerUrl = process.env.REACT_APP_WORKER_URL || 'https://invy-api.al-9f0.workers.dev';
      const response = await fetch(`${workerUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Token invalid, clear it
        logout();
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (provider) => {
    // Get base Worker URL (without /api suffix)
    const workerUrl = process.env.REACT_APP_WORKER_URL || 'https://invy-api.al-9f0.workers.dev';
    window.location.href = `${workerUrl}/api/auth/${provider}`;
  };

  const handleCallback = (newToken) => {
    setToken(newToken);
    localStorage.setItem('invy_token', newToken);
    fetchUserInfo(newToken);
  };

  const logout = async () => {
    if (token) {
      try {
        // Get base Worker URL (without /api suffix)
        const workerUrl = process.env.REACT_APP_WORKER_URL || 'https://invy-api.al-9f0.workers.dev';
        await fetch(`${workerUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    setUser(null);
    setToken(null);
    localStorage.removeItem('invy_token');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      handleCallback,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
